import type { PermissionRequest, QuestionRequest } from "@mux/sdk/v2/client"
import { createComputed, createMemo, createSignal, untrack, type Signal } from "solid-js"
import type { RequestRoute } from "@/transport/requests"
import type { GlobalStore } from "./types"
import { cmp } from "./utils"

/** The open requests a chat shows: its own first, then its delegated work's, oldest first. */
export type SurfacedRequests = { questions: QuestionRequest[]; permissions: PermissionRequest[] }

const NO_REQUESTS: SurfacedRequests = { questions: [], permissions: [] }

/**
 * The chats an asking chat's requests surface in, itself first. Until its
 * route arrives a request surfaces only in the chat that asked.
 */
export function requestSurfaces(routes: Readonly<Record<string, RequestRoute | undefined>>, sessionID: string): readonly string[] {
  return routes[sessionID]?.surfacesIn ?? [sessionID]
}

/**
 * Every open request indexed by the chats it surfaces in, and the chats
 * asking. The server's routes decide where a request surfaces; nothing here
 * derives it from chat parents or transcripts.
 */
export function indexOpenRequests(store: Pick<GlobalStore, "question" | "permission" | "request_route">) {
  const byChat = new Map<string, SurfacedRequests>()
  const askers = new Set<string>()
  // The entries of the chats an asker's requests surface in.
  const entries = (asker: string) => {
    askers.add(asker)
    return requestSurfaces(store.request_route, asker).map((chat) => {
      let entry = byChat.get(chat)
      if (!entry) byChat.set(chat, (entry = { questions: [], permissions: [] }))
      return entry
    })
  }
  for (const [asker, list] of Object.entries(store.question)) {
    if (list?.length) for (const entry of entries(asker)) entry.questions.push(...list)
  }
  for (const [asker, list] of Object.entries(store.permission)) {
    if (list?.length) for (const entry of entries(asker)) entry.permissions.push(...list)
  }
  for (const [chat, entry] of byChat) {
    const order = (a: { id: string; sessionID: string }, b: { id: string; sessionID: string }) =>
      Number(a.sessionID !== chat) - Number(b.sessionID !== chat) || cmp(a.id, b.id)
    entry.questions.sort(order)
    entry.permissions.sort(order)
  }
  return { byChat, askers: [...askers] }
}

const sameRequests = (a: SurfacedRequests, b: SurfacedRequests) =>
  a.questions.length === b.questions.length && a.permissions.length === b.permissions.length &&
  a.questions.every((request, index) => request === b.questions[index]) &&
  a.permissions.every((request, index) => request === b.permissions[index])

/** Open questions and permission asks, by the chats they surface in. */
export function createOpenRequests(store: Pick<GlobalStore, "question" | "permission" | "request_route">) {
  const index = createMemo(() => indexOpenRequests(store))
  // One signal per chat a view reads, so a request change re-runs only the
  // views of the chats whose requests changed.
  const chats = new Map<string, Signal<SurfacedRequests>>()
  createComputed(() => {
    const byChat = index().byChat
    for (const [chat, [, set]] of chats) set(byChat.get(chat) ?? NO_REQUESTS)
  })
  const chat = (sessionID: string) => {
    let signal = chats.get(sessionID)
    if (!signal) {
      signal = createSignal(untrack(index).byChat.get(sessionID) ?? NO_REQUESTS, { equals: sameRequests })
      chats.set(sessionID, signal)
    }
    return signal[0]
  }
  return {
    /** A chat's own open requests first, then its delegated work's, oldest first. */
    in: (sessionID: string) => chat(sessionID)(),
    /** The chats with open requests. */
    askers: () => index().askers,
    /** The chats an asking chat's requests surface in, itself first. */
    surfaces: (sessionID: string) => requestSurfaces(store.request_route, sessionID),
    /** The asking chat's route, which names it. */
    route: (sessionID: string) => store.request_route[sessionID],
  }
}
